import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { formatDate, formatToZMW, rootUrl, sumOfArray } from '../../../helpers';
import { useAuth } from '../../../context/auth-context';
import { FaBook, FaChartBar, FaEye, FaFileExcel, FaFolderOpen, FaPrint, FaSpinner, FaTimes } from 'react-icons/fa';
import { useOutletContext } from 'react-router-dom';
import * as XLSX from 'xlsx';

export default function SummaryCash({ startDate, endDate, triggerFetch }) {
  const [cash, setCash] = useState([]);
  const [open, setOpen] = useState([]);
  const [cashbook, setCashbook] = useState([]);
  const [selectedCashbook, setSelectedCashbook] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const auth = useAuth();
  const {allData, setAllData} = useOutletContext();

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await axios.get(rootUrl(`/getSingleCashByDate/${auth.user?.cid}~${startDate}~${endDate}`));
      setOpen(data.data.data.balances);
      setCash(data.data.data.cash);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const componentRef = useRef(null);
  
  function handleExcel() {
      const input = componentRef.current;
    
      if (!input) return;
    
      // Extract table or structured data from the ref
      const rows = Array.from(input.querySelectorAll('tr')).map((row) => {
        return Array.from(row.querySelectorAll('th, td')).map((cell) => cell.textContent);
      });
    
      // Create a worksheet and workbook
      const worksheet = XLSX.utils.aoa_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
    
      // Append the worksheet to the workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    
      // Export the workbook as an Excel file
      XLSX.writeFile(workbook, `cashsummary.xlsx`);
    }

  const handlePrint = () => {
    const printContent = componentRef.current;
    const printWindow = window.open("", "", "width=800,height=600");
    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>Cashbook</title>
          <script src="https://cdn.tailwindcss.com"></script>
          <style>
            @media print {
              body { margin: 20px; }
            }
          </style>
        </head>
        <body class="p-4">${printContent.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
  
    // Ensure Tailwind is applied before printing
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  };

  useEffect(() => {
    if (triggerFetch && startDate && endDate) {
      fetchData();
    }
  }, [triggerFetch]);

  const handleAction = async (id) => {
    const selected = allData.bank.find((res) => res.id === id);
    setSelectedCashbook(selected);
    setIsModalOpen(true);
    
    try {
      const data = await axios.get(rootUrl(`/getBankCash/${id}~${startDate}~${endDate}`));
      setCashbook(data.data.data);
    } catch (error) {
      console.error('Error fetching bank cash data:', error);
    }
  };

  const calculateBalance = () => {
    // Get the initial opening balance
    let balance = parseFloat(open.find((a) => a.bank == cashbook[0]?.bank)?.opening_balance) || 0;
  
    // Sort the cashbook data by date in ascending order
    const sortedCashbook = [...cashbook].sort((a, b) => new Date(a.date) - new Date(b.date));
  
    return sortedCashbook.map((row) => {
      balance += row.type === 'income' ? parseFloat(row.amount) : -parseFloat(row.amount);
      return { ...row, balance };
    });
  };
  const [groupedView, setGroupedView] = useState(false);


  const groupByVoucher = (data) => {
    const grouped = {};
    let runningBalance = 0;
  
    data.forEach(item => {
      if (!grouped[item.voucher]) {
        grouped[item.voucher] = {
          voucher: item.voucher,
          date: item.date,
          cheque: item.cheque,
          description: item.description,
          paye: item.paye,
          firstType: item.type,
          entries: [],
          totalIncome: 0,
          totalExpense: 0,
        };
      }
  
      grouped[item.voucher].entries.push(item);
  
      if (item.type === 'income') {
        grouped[item.voucher].totalIncome += parseFloat(item.amount);
      } else if (item.type === 'expense') {
        grouped[item.voucher].totalExpense += parseFloat(item.amount);
      }
    });
  
    // Now, convert to array and compute balance
    const groupedArray = Object.values(grouped).map(group => {
      runningBalance += group.totalIncome - group.totalExpense;
      return {
        ...group,
        balance: runningBalance,
      };
    });
  
    return groupedArray;
  };
  
  

  useEffect(()=>{
    calculateBalance()
  },[cashbook])
  return (
    <div>
      <h2 className="font-bold text-xl flex items-center">
        <FaBook /> Cashbooks Summary
      </h2>
      <hr className="my-2" />

      {loading ? (
        <div className="flex flex-col justify-center items-center h-64">
          <FaSpinner className="text-blue-950 text-6xl mb-4 animate-spin" />
          <p className="text-gray-600 text-lg">Loading cashbook data...</p>
        </div>
      ) : allData.bank !== undefined > 0 ? (
        <>
          <table className="w-full mt-4 border border-gray-300 shadow-lg rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-blue-950 text-white text-left">
                <th className="px-6 py-3 border-b border-gray-300 text-center">Cashbook</th>
                <th className="px-6 py-3 border-b border-gray-300 text-center">Opening Balance</th>
                <th className="px-6 py-3 border-b border-gray-300 text-center">Total Income</th>
                <th className="px-6 py-3 border-b border-gray-300 text-center">Total Expenses</th>
                <th className="px-6 py-3 border-b border-gray-300 text-center">Balance</th>
                <th className="px-6 py-3 border-b border-gray-300 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {allData.bank.map((res, index) => {
                const openingBalance = parseFloat(open.find((a) => a.bank == res.id)?.opening_balance) || 0;
                const totalIncome = parseFloat(cash.find((a) => a.bank == res.id && a.type == 'income')?.amount) || 0;
                const totalExpenses = parseFloat(cash.find((a) => a.bank == res.id && a.type == 'expense')?.amount) || 0;
                const balance = (parseFloat(open.find((a) => a.bank == res.id)?.opening_balance) || 0) + totalIncome - totalExpenses;

                return (
                  <tr key={res.id} className={`${index % 2 === 0 ? 'bg-gray-100' : 'bg-white'} hover:bg-gray-200 transition-all`}>
                    <td className="px-6 py-3 border-b border-gray-300">{res.name}</td>
                    <td className="px-6 py-3 border-b border-gray-300 text-center">{formatToZMW(openingBalance)}</td>
                    <td className="px-6 py-3 border-b border-gray-300 text-center text-blue-600">{formatToZMW(totalIncome)}</td>
                    <td className="px-6 py-3 border-b border-gray-300 text-center text-red-600">{formatToZMW(totalExpenses)}</td>
                    <td className="px-6 py-3 border-b border-gray-300 text-center font-semibold">{formatToZMW(balance)}</td>
                    <td className="px-6 py-3 border-b border-gray-300 text-center font-semibold">
                      <button onClick={() => handleAction(res.id)} className="p-1 hover:bg-gray-200 rounded">
                        <FaEye className="text-lg text-blue-600 hover:text-blue-800" />
                      </button>
                    </td>
                  </tr>
                );
              })}

              {/* Totals Row */}
              <tr className="bg-blue-950 text-white font-semibold">
                <td className="px-6 py-3 text-center">Total</td>
                <td className="px-6 py-3 text-center">
                  {formatToZMW(
                    allData.bank.reduce((sum, res) => sum + (parseFloat(open.find((a) => a.bank == res.id)?.opening_balance) || 0), 0)
                  )}
                </td>
                <td className="px-6 py-3 text-center text-blue-400">
                  {formatToZMW(
                    allData.bank.reduce((sum, res) => sum + (parseFloat(cash.find((a) => a.bank == res.id && a.type == 'income')?.amount) || 0), 0)
                  )}
                </td>
                <td className="px-6 py-3 text-center text-red-400">
                  {formatToZMW(
                    allData.bank.reduce((sum, res) => sum + (parseFloat(cash.find((a) => a.bank == res.id && a.type == 'expense')?.amount) || 0), 0)
                  )}
                </td>
                <td className="px-6 py-3 text-center">
                  {formatToZMW(
                    (
                      allData.bank.reduce((sum, res) => sum + (parseFloat(open.find((a) => a.bank == res.id)?.opening_balance) || 0), 0)
                    )+
                    allData.bank.reduce(
                      (sum, res) =>
                        sum +
                        (
                          (parseFloat(cash.find((a) => a.bank == res.id && a.type == 'income')?.amount) || 0) -
                          (parseFloat(cash.find((a) => a.bank == res.id && a.type == 'expense')?.amount) || 0)),
                      0
                    )
                  )}
                </td>
                <td className="px-6 py-3 text-center"></td>
              </tr>
            </tbody>
          </table>

          {/* Report Modal */}
          {isModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50  flex justify-center items-center">
              <div className="bg-white p-6 relative rounded-lg w-3/4 shadow-lg z-50">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-semibold">Report</h3>

                <div className='flex items-center justify-center'>
                <button
                        onClick={handlePrint}
                        className="bg-blue-950 m-2 text-white px-4 py-2 rounded"
                      >
                        <FaPrint/>
                      </button>

                      <button
                        onClick={handleExcel}
                        className="bg-blue-950 m-2 text-white px-4 py-2 rounded"
                      >
                        <FaFileExcel/>
                      </button>

                      <button
  onClick={() => setGroupedView(prev => !prev)}
  className="bg-blue-950 m-2 text-white px-4 py-2 rounded"
>
  {groupedView ? "Show Full List" : "Group by Voucher"}
</button>

                </div>
                  <button onClick={() => setIsModalOpen(false)}>
                    <FaTimes className="text-red-600 text-xl" />
                  </button>
                </div>
                <hr className="my-2" />


             

                      
                
                <div ref={componentRef} className='overflow-auto p-4 h-[80vh]'>
                
                <table className="w-full text-center mb-4">
  <tbody>
    <tr>
      <td colSpan="3" className="font-bold text-2xl">
        {allData.council[0]?.name.toUpperCase()}
      </td>
    </tr>
    <tr>
      <td colSpan="3" className="text-xl font-bold">
        {selectedCashbook?.name} : {selectedCashbook?.account_no}
      </td>
    </tr>
    <tr>
      <td colSpan="3" className="text-xl font-bold">
        Cash book from {formatDate(startDate)} to {formatDate(endDate)}
      </td>
    </tr>
  </tbody>
</table>

                <hr className='my-3' />
               
                <table class="w-[25%] text-left">
  <tbody>
    <tr>
      <td class="font-semibold">Opening Balance:</td>
      <td className='text-right'>{formatToZMW(parseFloat(open.find((a) => a.bank == selectedCashbook?.id)?.opening_balance) || 0)}</td>
    </tr>
    <tr>
      <td class="font-semibold">Total Income:</td>
      <td className='text-right'>{formatToZMW(parseFloat(cash.find((a) => a.bank == selectedCashbook?.id && a.type == 'income')?.amount) || 0)}</td>
    </tr>
    <tr>
      <td class="font-semibold">Total Expenses:</td>
      <td className='text-right'>{formatToZMW(parseFloat(cash.find((a) => a.bank == selectedCashbook?.id && a.type == 'expense')?.amount) || 0)}</td>
    </tr>
    <tr>
      <td class="font-semibold">Balance:</td>
      <td className='text-right'>{formatToZMW((parseFloat(open.find((a) => a.bank == selectedCashbook?.id)?.opening_balance) || 0) + (parseFloat(cash.find((a) => a.bank == selectedCashbook?.id && a.type == 'income')?.amount) || 0) - (parseFloat(cash.find((a) => a.bank == selectedCashbook?.id && a.type == 'expense')?.amount) || 0))}</td>
    </tr>
  </tbody>
</table>

<div className="">
      <table className="min-w-full bg-white shadow-md rounded-md">
      <thead>

  <tr className="bg-gray-200">
    <th className="px-4 py-2">Date</th>
    {!groupedView && <th className="px-4 py-2">COA</th>}
    <th className="px-4 py-2">Description</th>
    <th className="px-4 py-2">Voucher</th>
    <th className="px-4 py-2">Cheque</th>
    <th className="px-4 py-2">Payee</th>
    <th className="px-4 py-2">Debit</th>
    <th className="px-4 py-2">Credit</th>
    <th className="px-4 py-2">Balance</th>
  </tr>
</thead>

        <tbody>
        {cashbook.length > 0 ? (
  (groupedView ? groupByVoucher(calculateBalance()) : calculateBalance()).map((item, index) => (
    <tr key={index} className="border-t hover:bg-gray-100">
      <td className="px-4 py-2">{item.date.substring(0, 10)}</td>
      {!groupedView && (
        <td className="px-4 py-2">
          {allData?.budget?.find((a) => a.id === item.coa_id)?.coa || ''} :
          {allData.coa?.find(b => b.account == allData?.budget?.find((a) => a.id === item.coa_id)?.coa)?.name}
        </td>
      )}
      <td title={item.description} className="px-4 py-2">{item.description}</td>
      <td className="px-4 py-2">{item.voucher}</td>
      <td className="px-4 py-2">{item.cheque}</td>
      <td className="px-4 py-2">
  {(() => {
    const payeeId = item.paye;
    if (item.firstType === "income") {
      const customer = allData?.suppliers?.find((a) => a.id == payeeId);
      return customer ? `${customer.name}` : "";
    } else {
      const supplier = allData?.suppliers?.find((a) => a.id == payeeId);
      return supplier?.name || "";
    }
  })()}
</td>

      <td className="px-4 py-2">
        {formatToZMW(groupedView ? item.totalIncome : (item.type === 'income' ? item.amount : 0))}
      </td>
      <td className="px-4 py-2">
        {formatToZMW(groupedView ? item.totalExpense : (item.type === 'expense' ? item.amount : 0))}
      </td>
      <td className="px-4 py-2">
  {formatToZMW(groupedView ? item.balance : (item.balance || 0))}
</td>

    </tr>
  ))
) : (
  <tr>
    <td colSpan="9" className="text-center py-4">
      <div className="flex flex-col justify-center items-center">
        <FaFolderOpen className="text-gray-500 text-6xl mb-2" />
        <p className="text-gray-600">No data available</p>
      </div>
    </td>
  </tr>
)}


  <tr>
    <th colSpan={6}>Total</th>
    <th className="px-4 py-2">
      {formatToZMW(
        sumOfArray(
          (groupedView ? groupByVoucher(calculateBalance()) : calculateBalance())
            .map(a => groupedView ? a.totalIncome : (a.type === 'income' ? parseFloat(a.amount) : 0))
        )
      )}
    </th>
    <th className="px-4 py-2">
      {formatToZMW(
        sumOfArray(
          (groupedView ? groupByVoucher(calculateBalance()) : calculateBalance())
            .map(a => groupedView ? a.totalExpense : (a.type === 'expense' ? parseFloat(a.amount) : 0))
        )
      )}
    </th>
    <th></th>
  </tr>
</tbody>

      </table>
    </div>
              </div>
            </div>
            </div>
          )}
        </>
      ) : (
        <FaSpinner className="spinner" />
      )}
    </div>
    
  );
}
